
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { BagData, Filters, FilterOptions } from "@/types/bag";

export function useBags(page: number, pageSize: number, filters: Filters) {
  return useQuery({
    queryKey: ["bags", page, filters],
    queryFn: async () => {
      // Build the base query
      let baseQuery = supabase.from("coach_bag_real_data").select("*", { count: 'exact' });
      
      // Apply filters
      if (filters.category) {
        baseQuery = baseQuery.eq("Category", filters.category);
      }

      if (filters.year) {
        baseQuery = baseQuery.ilike("Years_in_Production", `%${filters.year}%`);
      }

      if (filters.color) {
        baseQuery = baseQuery.ilike("Colors", `%${filters.color}%`);
      }

      // Get paginated data with count, now ordering by style_number in descending order
      const { data, error, count } = await baseQuery
        .range((page - 1) * pageSize, page * pageSize - 1)
        .order("style_number", { ascending: false });

      if (error) throw error;

      return {
        bags: data as BagData[],
        totalCount: count ?? 0,
      };
    },
  });
}

export function useFilterOptions() {
  return useQuery({
    queryKey: ["filterOptions"],
    queryFn: async () => {
      const fetchUniqueValues = async (column: string) => {
        const { data } = await supabase
          .from("coach_bag_real_data")
          .select(column)
          .not(column, 'is', null);
        
        const uniqueValues = new Set<string>();
        data?.forEach((item: Record<string, any>) => {
          if (column === "Colors" && item[column]) {
            item[column].split(",").forEach((color: string) => {
              uniqueValues.add(color.trim());
            });
          } else if (column === "Years_in_Production" && item[column]) {
            const years = item[column].match(/\d{4}/g) || [];
            years.forEach(year => uniqueValues.add(year));
          } else if (item[column]) {
            uniqueValues.add(item[column]);
          }
        });
        
        return Array.from(uniqueValues).sort();
      };

      const [categories, years, colors] = await Promise.all([
        fetchUniqueValues("Category"),
        fetchUniqueValues("Years_in_Production"),
        fetchUniqueValues("Colors"),
      ]);

      return {
        categories,
        years,
        colors,
      } as FilterOptions;
    },
  });
}
